import {
  MeetingFragmentFragment,
  UpdateMeetingsLessonMutation,
  UpdateMeetingsLessonMutationVariables,
  UpdateMeetingsMutation,
  UpdateMeetingsMutationVariables
} from "@outschool/gql-frontend-generated";
import { gql, useMutation } from "@outschool/ui-apollo";
import { MeetingFragment } from "@outschool/ui-components-classroom";
import { dataIdFromObject } from "@outschool/ui-gql";
import _ from "lodash";
import React from "react";

import { validOptimisticResponse } from "../../shared/client/ApolloClient";
import {
  CreateMeetingsMutation,
  CreateMeetingsMutationVariables,
  CreateMeetingsWithLessonUidsMutation,
  CreateMeetingsWithLessonUidsMutationVariables,
  DeleteMeetingMutation,
  DeleteMeetingMutationVariables,
  TeacherSectionQuery,
  TeacherSectionQueryVariables
} from "../../shared/generated/graphql";
import { teacherSectionQuery } from "./SectionQueries";

import type { ExecutionResult } from "@outschool/ui-apollo";

export const createMeetingsMutation = gql`
  mutation createMeetings($sectionUid: ID!, $times: [StartEndTime!]!) {
    createMeetings(sectionUid: $sectionUid, times: $times) {
      ...MeetingFragment
      holiday {
        name
      }
    }
  }
  ${MeetingFragment}
`;

export function useCreateMeetingMutation({ section }: $TSFixMe) {
  const [mutate] = useMutation<
    CreateMeetingsMutation,
    CreateMeetingsMutationVariables
  >(createMeetingsMutation);

  return React.useMemo(() => {
    return [
      async (meetingTimes: $TSFixMe) => {
        const {
          // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
          data: { createMeetings: createdMeetings }
        } = await mutate({
          variables: {
            sectionUid: section.uid,
            times: meetingTimes.map((t: $TSFixMe) => ({
              start_time: t.start_time.toISOString(),
              end_time: t.end_time.toISOString()
            }))
          },
          optimisticResponse: validOptimisticResponse({
            __typename: "Mutation",
            createMeetings: meetingTimes.map((t: $TSFixMe) => ({
              __typename: "Meeting",
              start_time: t.start_time.toISOString(),
              end_time: t.end_time.toISOString(),
              deleted_at: null,
              uid: Math.random(),
              holiday: null
            }))
          }),
          // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
          update(proxy, { data: { createMeetings } }) {
            const data = proxy.readQuery<
              TeacherSectionQuery,
              TeacherSectionQueryVariables
            >({
              query: teacherSectionQuery,
              variables: { uid: section.uid }
            });
            if (!data?.section?.meetings) {
              return;
            }
            const meetings = _.sortBy(
              [...data.section.meetings, ...createMeetings],
              "start_time"
            );
            proxy.writeQuery({
              query: teacherSectionQuery,
              variables: { uid: section.uid },
              data: {
                ...data,
                section: { ...data.section, meetings }
              }
            });
          },
          // We have to refetch to make sure that we get the latest conflict checks
          refetchQueries() {
            return [
              {
                query: teacherSectionQuery,
                variables: { uid: section.uid }
              }
            ];
          }
        });
        return createdMeetings;
      }
    ];
  }, [mutate, section.uid]);
}

export const createMeetingsWithLessonUidsMutation = gql`
  mutation createMeetingsWithLessonUids(
    $sectionUid: ID!
    $meetingDetails: [MeetingDetails!]!
  ) {
    createMeetingsWithLessonUids(
      sectionUid: $sectionUid
      meetingDetails: $meetingDetails
    ) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useCreateMeetingsWithLessonUidsMutation({ section }: $TSFixMe) {
  const [mutate] = useMutation<
    CreateMeetingsWithLessonUidsMutation,
    CreateMeetingsWithLessonUidsMutationVariables
  >(createMeetingsWithLessonUidsMutation);

  const mutation = React.useCallback(
    async (meetingDetails: $TSFixMe) => {
      const {
        // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
        data: { createMeetingsWithLessonUids: createdMeetings }
      } = await mutate({
        variables: {
          sectionUid: section.uid,
          meetingDetails: meetingDetails.map(
            ({ start_time, end_time, lesson_uid }: $TSFixMe) => ({
              start_time: start_time.toISOString(),
              end_time: end_time.toISOString(),
              lesson_uid
            })
          )
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          createMeetingsWithLessonUids: meetingDetails.map(
            (details: $TSFixMe) => ({
              __typename: "Meeting",
              lesson_uid: details.lesson_uid,
              start_time: details.start_time.toISOString(),
              end_time: details.end_time.toISOString(),
              deleted_at: null,
              uid: Math.random(),
              holiday: null
            })
          )
        }),
        // @ts-ignore TS(2339): Property 'createMeetings' does not exist on type '... Remove this comment to see the full error message
        update(proxy, { data: { createMeetingsWithLessonUids } }) {
          const data = proxy.readQuery<
            TeacherSectionQuery,
            TeacherSectionQueryVariables
          >({
            query: teacherSectionQuery,
            variables: { uid: section.uid }
          });
          if (!data?.section?.meetings) {
            return;
          }

          const meetings = _.sortBy(
            [...data.section.meetings, ...createMeetingsWithLessonUids],
            "start_time"
          );
          proxy.writeQuery({
            query: teacherSectionQuery,
            variables: { uid: section.uid },
            data: {
              ...data,
              section: { ...data.section, meetings }
            }
          });
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid }
            }
          ];
        }
      });
      return createdMeetings;
    },
    [mutate, section.uid]
  );
  return [mutation];
}

export const updateMeetingsMutation = gql`
  mutation updateMeetings($updateMeetingsInput: [UpdateMeetingInput!]!) {
    updateMeetings(updateMeetingsInput: $updateMeetingsInput) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useUpdateMeetings({
  section
}: $TSFixMe): [
  (
    updateMeetingsInput: UpdateMeetingsMutationVariables["updateMeetingsInput"]
  ) => Promise<void>,
  { isUpdatingMeetings: boolean }
] {
  const [mutate, { loading }] = useMutation<
    UpdateMeetingsMutation,
    UpdateMeetingsMutationVariables
  >(updateMeetingsMutation);

  const mutation = React.useCallback(
    async (updateMeetingsInput: $TSFixMe) => {
      await mutate({
        variables: {
          updateMeetingsInput
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid }
            }
          ];
        }
      });
    },
    [mutate, section.uid]
  );
  return [mutation, { isUpdatingMeetings: loading }];
}

export const updateMeetingsLessonMutation = gql`
  mutation updateMeetingsLesson(
    $updateMeetingsLessonInput: [UpdateMeetingLessonInput!]!
  ) {
    updateMeetingsLesson(
      updateMeetingsLessonInput: $updateMeetingsLessonInput
    ) {
      ...MeetingFragment
      holiday {
        name
      }
      lesson {
        uid
        title
      }
    }
  }
  ${MeetingFragment}
`;

export function useUpdateMeetingsLesson({
  section
}: $TSFixMe): [
  (
    updateMeetingsLessonInput: UpdateMeetingsLessonMutationVariables["updateMeetingsLessonInput"]
  ) => Promise<void>,
  { isAssigningOrUnassigningLesson: boolean }
] {
  const [mutate, { loading }] = useMutation<
    UpdateMeetingsLessonMutation,
    UpdateMeetingsLessonMutationVariables
  >(updateMeetingsLessonMutation);

  const mutation = React.useCallback(
    async (updateMeetingsLessonInput: $TSFixMe) => {
      await mutate({
        variables: {
          updateMeetingsLessonInput
        },
        // We have to refetch to make sure that we get the latest conflict checks
        refetchQueries() {
          return [
            {
              query: teacherSectionQuery,
              variables: { uid: section.uid }
            }
          ];
        }
      });
    },
    [mutate, section.uid]
  );
  return [mutation, { isAssigningOrUnassigningLesson: loading }];
}

export const deleteMeetingMutation = gql`
  mutation deleteMeeting($uid: ID!) {
    deleteMeeting(uid: $uid)
  }
`;

export function useDeleteMeetingMutation({
  section
}: $TSFixMe): [
  (meetingUid: string) => Promise<ExecutionResult<any>>,
  { isDeletingMeeting: boolean }
] {
  const [mutate, { loading }] = useMutation<
    DeleteMeetingMutation,
    DeleteMeetingMutationVariables
  >(deleteMeetingMutation);

  return React.useMemo(() => {
    return [
      (meetingUid: $TSFixMe) => {
        return mutate({
          variables: { uid: meetingUid },
          optimisticResponse: validOptimisticResponse({
            __typename: "Mutation",
            deleteMeeting: true
          }),
          // @ts-ignore TS(2339): Property 'deleteMeeting' does not exist on type 'D... Remove this comment to see the full error message
          update(proxy, { data: { deleteMeeting } }) {
            if (!deleteMeeting) {
              return;
            }
            const data = proxy.readQuery<
              TeacherSectionQuery,
              TeacherSectionQueryVariables
            >({
              query: teacherSectionQuery,
              variables: { uid: section.uid }
            });
            if (!data?.section) {
              return;
            }
            const newSection = { ...data.section };
            const deletedMeeting = _.find(
              newSection.meetings,
              m => m.uid === meetingUid
            );
            if (!deletedMeeting) {
              return;
            }

            newSection.meetings = newSection.meetings.filter(
              m => m.uid !== meetingUid
            );
            newSection.deletedMeetings = [
              ...newSection.deletedMeetings,
              { ...deletedMeeting, deleted_at: new Date() }
            ];
            proxy.writeQuery({
              query: teacherSectionQuery,
              variables: { uid: section.uid },
              data: { ...data, section: newSection }
            });
          },
          // We have to refetch to make sure that we get the latest conflict checks
          refetchQueries() {
            return [
              {
                query: teacherSectionQuery,
                variables: { uid: section.uid }
              }
            ];
          }
        });
      },
      { isDeletingMeeting: loading }
    ];
  }, [mutate, loading, section.uid]);
}

export function useCancelOneOnOneMeetingMutation(): [
  ({
    uid,
    cancelReason,
    cancelNote,
    refundAmountCents,
    reducesTeacherPayout
  }: {
    uid: string;
    cancelReason: string;
    cancelNote: string;
    refundAmountCents?: number;
    reducesTeacherPayout?: boolean;
  }) => Promise<ExecutionResult<any>>,
  { isCancelingOneOnOneMeeting: boolean }
] {
  const [cancelOneOnOneMeeting, { loading }] = useMutation(
    gql`
      mutation cancelOneOnOneMeeting(
        $uid: ID!
        $cancelReason: String!
        $cancelNote: String
        $refundAmountCents: Int
        $reducesTeacherPayout: Boolean
      ) {
        cancelOneOnOneMeeting(
          uid: $uid
          cancelReason: $cancelReason
          cancelNote: $cancelNote
          refundAmountCents: $refundAmountCents
          reducesTeacherPayout: $reducesTeacherPayout
        ) {
          ...MeetingFragment
        }
      }
      ${MeetingFragment}
    `,
    {
      update: (proxy, result) => {
        const canceledMeeting = result?.data?.cancelOneOnOneMeeting;

        proxy.writeFragment({
          fragment: MeetingFragment,
          fragmentName: "MeetingFragment",
          id: dataIdFromObject(canceledMeeting),
          data: canceledMeeting
        });
      }
    }
  );

  return [
    React.useCallback(
      ({
        uid,
        cancelReason,
        cancelNote,
        refundAmountCents,
        reducesTeacherPayout = true
      }: {
        uid: string;
        cancelReason: string;
        cancelNote: string;
        refundAmountCents: number;
        reducesTeacherPayout: boolean;
      }) =>
        cancelOneOnOneMeeting({
          variables: {
            uid,
            cancelReason,
            cancelNote,
            refundAmountCents,
            reducesTeacherPayout
          }
        }),
      [cancelOneOnOneMeeting]
    ),
    { isCancelingOneOnOneMeeting: loading }
  ];
}

export const cancelAllOneOnOneSectionMeetingsMutation = gql`
  mutation cancelAllOneOnOneSectionMeetings(
    $sectionUid: ID!
    $cancelReason: String!
    $cancelNote: String
    $reducesTeacherPayout: Boolean
  ) {
    cancelAllOneOnOneSectionMeetings(
      sectionUid: $sectionUid
      cancelReason: $cancelReason
      cancelNote: $cancelNote
      reducesTeacherPayout: $reducesTeacherPayout
    )
  }
`;

export function useCancelRecurringOneOnOneMeetingMutation(): [
  ({
    uid,
    cancelReason,
    cancelNote,
    refundAmountCents,
    reducesTeacherPayout
  }: {
    uid: string;
    cancelReason: string;
    cancelNote: string;
    refundAmountCents?: number;
    reducesTeacherPayout?: boolean;
  }) => Promise<ExecutionResult<any>>,
  { isCancelingRecurringOneOnOneMeeting: boolean }
] {
  const [cancelRecurringOneOnOneMeeting, { loading }] = useMutation(
    gql`
      mutation cancelOneOnOneRecurringMeetings(
        $uid: ID!
        $cancelReason: String!
        $cancelNote: String
        $reducesTeacherPayout: Boolean
      ) {
        cancelOneOnOneRecurringMeetings(
          uid: $uid
          cancelReason: $cancelReason
          cancelNote: $cancelNote
          reducesTeacherPayout: $reducesTeacherPayout
        )
      }
    `,
    {
      update: (proxy, result) => {
        if (!result) {
          return;
        }
        for (const meetingUid of result.data.cancelOneOnOneRecurringMeetings) {
          const fragmentData = proxy.readFragment<MeetingFragmentFragment>({
            fragment: MeetingFragment,
            fragmentName: "MeetingFragment",
            id: meetingUid + "Meeting"
          });
          if (fragmentData) {
            const updatedFragment = {
              ...fragmentData,
              deleted_at: new Date().toString()
            };
            proxy.writeFragment({
              fragment: MeetingFragment,
              id: meetingUid + "Meeting",
              data: updatedFragment
            });
          }
        }
      }
    }
  );

  return [
    React.useCallback(
      ({
        uid,
        cancelReason,
        cancelNote,
        reducesTeacherPayout = true
      }: {
        uid: string;
        cancelReason: string;
        cancelNote: string;
        reducesTeacherPayout: boolean;
      }) =>
        cancelRecurringOneOnOneMeeting({
          variables: {
            uid,
            cancelReason,
            cancelNote,
            reducesTeacherPayout
          }
        }),
      [cancelRecurringOneOnOneMeeting]
    ),
    { isCancelingRecurringOneOnOneMeeting: loading }
  ];
}

export function useRescheduleMeetingMutation() {
  const [rescheduleMeeting] = useMutation(
    gql`
      mutation rescheduleMeeting(
        $uid: ID!
        $startTime: DateTime!
        $endTime: DateTime!
        $rescheduleFutureRecurringMeetings: Boolean!
      ) {
        rescheduleMeeting(
          uid: $uid
          startTime: $startTime
          endTime: $endTime
          rescheduleFutureRecurringMeetings: $rescheduleFutureRecurringMeetings
        ) {
          ...MeetingFragment
        }
      }
      ${MeetingFragment}
    `,
    {
      refetchQueries: ["EnrolledClassDetails"],
      update: (proxy, result) => {
        if (result?.data?.rescheduleMeeting) {
          for (const meeting of result?.data?.rescheduleMeeting ?? []) {
            proxy.writeFragment({
              fragment: MeetingFragment,
              id: dataIdFromObject(meeting),
              data: meeting
            });
          }
        }
      }
    }
  );

  return React.useCallback(
    ({
      uid,
      startTime,
      endTime,
      rescheduleFutureRecurringMeetings
    }: {
      uid: string;
      startTime: Date;
      endTime: Date;
      rescheduleFutureRecurringMeetings: Boolean;
    }) =>
      rescheduleMeeting({
        variables: {
          uid,
          startTime,
          endTime,
          rescheduleFutureRecurringMeetings
        }
      }),
    [rescheduleMeeting]
  );
}
